/**
 * AlgoBuilderX API Service
 *
 * Component-based strategy builder API integration.
 * Base URL: /api/algobuilderx
 */

import { baseURL } from '../config';

const API_BASE = `${baseURL}/api/algobuilderx`;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

const getAuthHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

const handleResponse = async (response) => {
  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || `API error: ${response.status}`);
    error.status = response.status;
    error.errors = data.errors || [];
    throw error;
  }

  return data.success !== undefined ? data.data : data;
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT ENDPOINTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Get all available components */
export const getComponents = async (token) => {
  const response = await fetch(`${API_BASE}/components`, {
    method: 'GET',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Get components by type: Indicator, EntryRule, ExitRule, LotSizer, RiskManager */
export const getComponentsByType = async (token, type) => {
  const response = await fetch(`${API_BASE}/components/type/${type}`, {
    method: 'GET',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Get component details by ID */
export const getComponentById = async (token, componentId) => {
  const response = await fetch(`${API_BASE}/components/${componentId}`, {
    method: 'GET',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

// ═══════════════════════════════════════════════════════════════════════════════
// STRATEGY ENDPOINTS
// ═══════════════════════════════════════════════════════════════════════════════

/** Create a new strategy */
export const createStrategy = async (token, strategyConfig) => {
  const response = await fetch(`${API_BASE}/strategies`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(strategyConfig)
  });
  return handleResponse(response);
};

/** Get all strategies */
export const getStrategies = async (token) => {
  const response = await fetch(`${API_BASE}/strategies`, {
    method: 'GET',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Get strategy by ID */
export const getStrategyById = async (token, strategyId) => {
  const response = await fetch(`${API_BASE}/strategies/${strategyId}`, {
    method: 'GET',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Update strategy */
export const updateStrategy = async (token, strategyId, updates) => {
  const response = await fetch(`${API_BASE}/strategies/${strategyId}`, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify(updates)
  });
  return handleResponse(response);
};

/** Delete strategy */
export const deleteStrategy = async (token, strategyId) => {
  const response = await fetch(`${API_BASE}/strategies/${strategyId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Validate strategy config */
export const validateStrategy = async (token, strategyConfig) => {
  const response = await fetch(`${API_BASE}/strategies/validate`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(strategyConfig)
  });
  return handleResponse(response);
};

// ═══════════════════════════════════════════════════════════════════════════════
// STRATEGY CONTROL
// ═══════════════════════════════════════════════════════════════════════════════

/** Start strategy */
export const startStrategy = async (token, strategyId) => {
  const response = await fetch(`${API_BASE}/strategies/${strategyId}/start`, {
    method: 'POST',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Stop strategy */
export const stopStrategy = async (token, strategyId) => {
  const response = await fetch(`${API_BASE}/strategies/${strategyId}/stop`, {
    method: 'POST',
    headers: getAuthHeaders(token)
  });
  return handleResponse(response);
};

/** Hot-reload parameters */
export const updateStrategyParameters = async (token, strategyId, parameters) => {
  const response = await fetch(`${API_BASE}/strategies/${strategyId}/parameters`, {
    method: 'PATCH',
    headers: getAuthHeaders(token),
    body: JSON.stringify(parameters)
  });
  return handleResponse(response);
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

export const COMPONENT_TYPES = {
  INDICATOR: 'Indicator',
  ENTRY_RULE: 'EntryRule',
  EXIT_RULE: 'ExitRule',
  LOT_SIZER: 'LotSizer',
  RISK_MANAGER: 'RiskManager'
};
